import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSync } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import { encaisserCouverture } from "../../../store/actions/couverturesActions"
import { getOpenerId } from "../../../utilities/session"
import { chf, ui, useEtroit } from "./couverturesUi"

// « Encaisser la couverture » (GE-60, maquette #m-encaisser) : la fenêtre
// EST la confirmation du bouton « Encaisser x.– » de la carte (règle UI V2).
// Crée l'encaissement assistance lié côté serveur (moyen Facture, payeur
// GE-58, n° de dossier, libellé avec la réf. d'ordre).
const Champ = ({ label, full = false, children }) => (
	<div style={full ? { gridColumn: "1 / -1" } : null}>
		<label style={ui.fieldLabel}>{label}</label>
		{children}
	</div>
)

// Montant saisi (contre-revue I11) : chiffres, séparateur de milliers « ' »
// ou espace, UN séparateur décimal (« . » ou « , »). Tout le reste —
// « 540abc », « 1.080,50 », « 1,080.50 » — est refusé (NaN), jamais lu faux.
export const lireMontant = (brut) => {
	const texte = String(brut || "").trim()
	if (!/^[0-9.,'’ ]+$/.test(texte)) return NaN
	const sansMilliers = texte.replace(/['’ ]/g, "")
	if ((sansMilliers.match(/[.,]/g) || []).length > 1) return NaN
	if (!/^\d+([.,]\d{1,2})?$/.test(sansMilliers)) return NaN
	return parseFloat(sansMilliers.replace(",", "."))
}

const libelleDefaut = (c) => {
	const refs = [c.ref_dossier, c.ref_ordre].filter(Boolean).join(" / ")
	return `Couverture ${c.assistance}${refs ? ` ${refs}` : ""}`
}

const EncaisserCouvertureModal = ({
	couverture: c,
	ficheId,
	reste,
	onClose,
	onDone,
}) => {
	const dispatch = useDispatch()
	const etroit = useEtroit()
	const [montant, setMontant] = useState(
		c.propose !== null && c.propose !== undefined
			? Number(c.propose).toFixed(2)
			: "",
	)
	const [numDossier, setNumDossier] = useState(c.ref_dossier || "")
	const [libelle, setLibelle] = useState(libelleDefaut(c))
	const [etat, setEtat] = useState({ saving: false, saved: false })
	const [erreur, setErreur] = useState(null)

	const valeur = lireMontant(montant)
	const valide = !Number.isNaN(valeur) && valeur > 0

	const confirmer = () => {
		if (!valide || etat.saving || etat.saved) return
		setErreur(null)
		setEtat({ saving: true, saved: false })
		dispatch(
			encaisserCouverture(c.id, {
				// Garde du verrou de fiche (revue I1) : même opener que le save.
				opener: getOpenerId(),
				montant: valeur,
				num_dossier: numDossier,
				libelle,
			}),
		).then((res) => {
			if (!res || res.erreur) {
				setErreur(res && res.erreur)
				setEtat({ saving: false, saved: false })
				return
			}
			setEtat({ saving: false, saved: true })
			setTimeout(() => onDone(res), 600)
		})
	}

	const refs = [c.ref_dossier, c.ref_ordre].filter(Boolean).join(" / ")
	return (
		<CustomModal
			title={`Encaisser la couverture ${c.assistance}`}
			subtitle={[`Fiche ${ficheId}`, refs].filter(Boolean).join(" · ")}
			onClose={etat.saving ? () => {} : onClose}
			showCloseButton={!etat.saving}
			maxWidth={600}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: etat.saving,
				},
				{
					title: etat.saving ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : etat.saved ? (
						<FontAwesomeIcon icon={faCheck} />
					) : (
						"Confirmer l'encaissement"
					),
					styleName: "primary",
					onClick: confirmer,
					disabled: !valide || etat.saving || etat.saved,
				},
			]}
		>
			<div style={ui.form(etroit ? 1 : 2)}>
				<Champ label="Payé par">
					<input
						style={ui.input}
						value={`Assistance — ${c.assistance}`}
						disabled
					/>
				</Champ>
				<Champ label="Moyen">
					<input style={ui.input} value="Facture" disabled />
				</Champ>
				<Champ label="Montant">
					<input
						style={{ ...ui.input, textAlign: "right" }}
						inputMode="decimal"
						value={montant}
						onChange={(e) => setMontant(e.target.value)}
						autoFocus
					/>
					{String(montant).trim() !== "" && Number.isNaN(valeur) && (
						<div style={{ ...ui.erreur, marginTop: 6 }}>
							Montant invalide (ex. 1'080.50).
						</div>
					)}
				</Champ>
				<Champ label="N° de dossier">
					<input
						style={ui.input}
						value={numDossier}
						onChange={(e) => setNumDossier(e.target.value)}
						maxLength={50}
					/>
				</Champ>
				<Champ label="Libellé" full>
					<input
						style={ui.input}
						value={libelle}
						onChange={(e) => setLibelle(e.target.value)}
						maxLength={200}
					/>
				</Champ>
			</div>
			<div style={ui.apercu}>
				{c.plafond != null ? (
					<>
						Montant proposé = min(disponible{" "}
						<b>{chf(c.disponible)}</b>, reste à payer de la fiche{" "}
						<b>{chf(reste)}</b>).{" "}
					</>
				) : (
					<>Plafond non précisé : montant à saisir. </>
				)}
				L'encaissement est <b>relié à la couverture</b>
				{c.message_id ? " (et à son e-mail)" : ""}. La quittance
				indiquera « Payé par : {c.assistance}
				{numDossier.trim() ? ` — dossier ${numDossier.trim()}` : ""} ».
			</div>
			{erreur && (
				<div style={ui.erreur}>
					{erreur.message || "L'encaissement a échoué."}
				</div>
			)}
		</CustomModal>
	)
}

export default EncaisserCouvertureModal
